/**
 * The app-wide toast (<x-toast /> in the main layout), as the `toastStore` Alpine store.
 *
 * It also follows the prompts Draft an article is writing (D99 of
 * documentation/pr-plans/insights-simplify-plan.md, section 4.4): the drawer tells the browser about a run
 * it starts or follows (`draft-run-followed`), which is remembered in localStorage, so every open tab of the
 * app knows it. While any run waits, each tab asks the run's status route every few seconds
 * (DraftRunStatusController). When one has ended, the first tab to see it shows the toast, with Open, which
 * lands on Insights with the drawer open on the run; it claims the run in localStorage first, so the toast
 * shows once. A tab whose drawer is open on the run marks it open, and the drawer says the end itself
 * (`draft-run-seen`), so no toast repeats it.
 */
const DRAFT_RUNS_KEY = 'bm-draft-runs';
const DRAFT_SHOWN_PREFIX = 'bm-draft-run-shown:';
const DRAFT_OPEN_PREFIX = 'bm-draft-run-open:';
const DRAFT_POLL_MS = 5000;
// An open drawer marks its run every 2 seconds; a mark older than this is a drawer that went away.
const DRAFT_OPEN_FRESH_MS = 6000;
// Far past a run's longest wait (ArticlePromptRun::STALE_AFTER_MINUTES); a run still remembered then is dropped.
const DRAFT_FORGET_AFTER_MS = 6 * 60 * 60 * 1000;
const DRAFT_CLAIM_WAIT_MS = 150;
// A claim is kept a day, long past any tab that could still see its run end, then dropped.
const DRAFT_CLAIM_KEEP_MS = 24 * 60 * 60 * 1000;
// A toast that carries a link stays long enough to reach it, and waits while it is hovered or holds focus.
const LINK_DURATION_MS = 20000;

function readStorage(key, fallback) {
    try {
        const value = window.localStorage.getItem(key);

        return value === null ? fallback : JSON.parse(value);
    } catch (error) {
        return fallback;
    }
}

function writeStorage(key, value) {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        // Storage refused (a private window): the run is followed in this tab only, for as long as it lives.
    }
}

function readDraftRuns() {
    const runs = readStorage(DRAFT_RUNS_KEY, []);

    return Array.isArray(runs) ? runs.filter((run) => run && Number.isInteger(run.runId) && typeof run.statusUrl === 'string') : [];
}

export default () => ({
  toasts: [],
  duration: 5000,
  draftWatcherStarted: false,
  draftTimer: null,

  /**
   * `persistent` opts a toast out of the 5s auto-dismiss, for the two session-expiry messages only.
   * Both have to survive being unread: one is raised while VoiceOver is still announcing a freshly
   * loaded page, and the other fires on a page nobody has touched for ten seconds, so by definition
   * there is nobody there to see it go. Everything else still dismisses itself, including the notice
   * on each failed click, so repeated failures cannot stack up toasts that stay.
   */
  addToast({message, description, icon, link, persistent = false}) {
    const uuid = Math.random().toString(36).substr(2, 9);
    const hasLink = link && typeof link.url === 'string' && typeof link.label === 'string';
    const duration = hasLink ? LINK_DURATION_MS : this.duration;
    const timeoutId = persistent ? null : setTimeout(() => {
      this.removeToastByUuid(uuid);
    }, duration);

    this.toasts.push({
      uuid,
      message,
      description,
      icon: icon,
      link: hasLink ? {url: link.url, label: link.label} : null,
      duration,
      persistent,
      show: true,
      timeoutId
    });
  },

  removeToastByUuid(uuid) {
    const index = this.toasts.findIndex(t => t.uuid === uuid);
    if (index === -1) return;
    this.removeToast(index);
  },

  removeToast(index) {
    clearTimeout(this.toasts[index].timeoutId);
    this.toasts[index].show = false;
  },

  pauseToast(index) {
    if(!this.toasts[index]) return;
    clearTimeout(this.toasts[index].timeoutId);
  },

  resumeToast(index) {
    // Moving the pointer away must not start a dismissal timer on a toast that has none, or hovering
    // a persistent notice would quietly convert it into a 5s one.
    if(!this.toasts[index] || this.toasts[index].persistent) return;
    const uuid = this.toasts[index].uuid;
    this.toasts[index].timeoutId = setTimeout(() => {
      this.removeToastByUuid(uuid);
    }, this.toasts[index].duration ?? this.duration);
  },

  /** Started once per tab by the layout's toast. */
  watchDraftRuns() {
    if (this.draftWatcherStarted) {
      return;
    }

    this.draftWatcherStarted = true;
    this.pruneDraftClaims();
    window.addEventListener('draft-run-followed', (event) => this.rememberDraftRun(event.detail ?? {}));
    window.addEventListener('draft-run-seen', (event) => this.forgetDraftRun(event.detail?.runId));
    // Another tab started or ended a run: follow the shared list at once.
    window.addEventListener('storage', (event) => {
      if (event.key === DRAFT_RUNS_KEY) {
        this.scheduleDraftPoll(0);
      }
    });
    this.scheduleDraftPoll(DRAFT_POLL_MS);
  },

  rememberDraftRun({runId, siteId, statusUrl}) {
    if (!Number.isInteger(runId) || typeof statusUrl !== 'string') {
      return;
    }

    const runs = readDraftRuns();

    if (!runs.some((run) => run.runId === runId)) {
      runs.push({runId, siteId, statusUrl, at: Date.now()});
      writeStorage(DRAFT_RUNS_KEY, runs);
    }

    this.scheduleDraftPoll(DRAFT_POLL_MS);
  },

  forgetDraftRun(runId) {
    writeStorage(DRAFT_RUNS_KEY, readDraftRuns().filter((run) => run.runId !== runId));
  },

  scheduleDraftPoll(delay) {
    clearTimeout(this.draftTimer);
    this.draftTimer = readDraftRuns().length === 0 ? null : setTimeout(() => this.pollDraftRuns(), delay);
  },

  async pollDraftRuns() {
    for (const run of readDraftRuns()) {
      if (Date.now() - run.at > DRAFT_FORGET_AFTER_MS) {
        this.forgetDraftRun(run.runId);
        continue;
      }

      const answer = await this.askDraftRun(run);

      if (answer === null || answer.status === 'running' || this.isDraftRunOpen(run.runId)) {
        continue;
      }

      const isMine = await this.claimDraftRun(run.runId);

      this.forgetDraftRun(run.runId);

      if (isMine && typeof answer.message === 'string') {
        this.addToast({message: answer.message, icon: answer.status === 'done' ? 'success' : 'error', link: answer.link});
      }
    }

    this.scheduleDraftPoll(DRAFT_POLL_MS);
  },

  /** The run's status, or null to ask again later. A run this person may no longer see is forgotten. */
  async askDraftRun(run) {
    try {
      const response = await fetch(run.statusUrl, {
        headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
        credentials: 'same-origin',
      });

      if (response.status === 403 || response.status === 404) {
        this.forgetDraftRun(run.runId);

        return null;
      }

      return response.ok ? await response.json() : null;
    } catch (error) {
      // Offline or a deploy in progress: the next poll asks again.
      return null;
    }
  },

  isDraftRunOpen(runId) {
    try {
      const mark = Number(window.localStorage.getItem(DRAFT_OPEN_PREFIX + runId));

      return mark > 0 && Date.now() - mark < DRAFT_OPEN_FRESH_MS;
    } catch (error) {
      return false;
    }
  },

  /**
   * Whether this tab shows the run's toast: it writes its own token, waits a moment for any tab that wrote
   * at the same time, and shows the toast only if its token is the one left.
   */
  async claimDraftRun(runId) {
    const key = DRAFT_SHOWN_PREFIX + runId;
    const token = Math.random().toString(36).slice(2) + ':' + Date.now();

    try {
      if (window.localStorage.getItem(key) !== null) {
        return false;
      }

      window.localStorage.setItem(key, token);
      await new Promise((resolve) => setTimeout(resolve, DRAFT_CLAIM_WAIT_MS));

      return window.localStorage.getItem(key) === token;
    } catch (error) {
      // Without storage this tab cannot tell another's claim apart, so it shows the toast itself.
      return true;
    }
  },

  pruneDraftClaims() {
    try {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith(DRAFT_SHOWN_PREFIX))
        .filter((key) => !(Date.now() - Number(String(window.localStorage.getItem(key)).split(':')[1]) < DRAFT_CLAIM_KEEP_MS))
        .forEach((key) => window.localStorage.removeItem(key));
    } catch (error) {
      // Storage refused: there is nothing to prune.
    }
  }
});
